import { useCallback, useEffect, useMemo, useState } from "react";
import { useStickyPlayerContext } from "../../../../common/StickyPlayerContext";
import { useVideoAnalyticsContext } from "../../contexts/VideoAnalyticsContext";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import {
  PlayerType,
  StickyPlayerEventLabel,
  VideoEvent,
  getPlayerType
} from "../../../lib/analytics/utils";
import {
  getStickyPlayerContext,
  shouldOpenSticky,
  shouldSendStickyAnalytics,
  shouldStickyVideoClose,
  shouldStickyVideoOpen
} from "./utils";
import { useIntersectionContext } from "../../contexts/IntersectionContext/IntersectionProvider";

export const useStickyPlayer = (config, stickyPlayerContext) => {
  const { uniqueId } = useVideoDataContext();
  const { sendAnalyticsEvent, setPlayerType } = useVideoAnalyticsContext();
  const { isInViewPort } = useIntersectionContext();

  const stickyPlayerHookContext = useStickyPlayerContext();

  const { playingId, openId, updateStickyContext } = useMemo(
    () => getStickyPlayerContext(stickyPlayerContext, stickyPlayerHookContext),
    [stickyPlayerContext, stickyPlayerHookContext]
  );

  const [isStickyEnabled, setIsStickyEnabled] = useState(false);
  const [isOpenSticky, setIsOpenSticky] = useState(false);
  // Used to override the global sticky player context, and keep the sticky video player open during playthrough
  const [overrideStickyContext, setOverrideStickyContext] = useState(false);

  useEffect(() => {
    const openSticky = shouldOpenSticky(
      overrideStickyContext,
      isStickyEnabled,
      openId,
      playingId,
      uniqueId
    );

    // check if the sticky video player should close SPECIFICALLY DUE TO OTHER MEDIA STARTING
    if (
      shouldSendStickyAnalytics(
        isOpenSticky,
        overrideStickyContext,
        playingId,
        uniqueId,
        openId
      )
    ) {
      sendAnalyticsEvent(VideoEvent.STICKY_PLAYER_CLOSE, {
        label: StickyPlayerEventLabel.OTHER_MEDIA_CLOSE
      });
    }

    setIsOpenSticky(openSticky);
  }, [
    openId,
    playingId,
    uniqueId,
    isStickyEnabled,
    overrideStickyContext,
    isOpenSticky,
    sendAnalyticsEvent
  ]);

  // Whenever the orca player is open sticky, we need to reflect that in analytics
  useEffect(() => {
    setPlayerType(isOpenSticky ? PlayerType.STICKY : getPlayerType(config));
  }, [isOpenSticky, config, setPlayerType]);

  const handleStickyOpen = useCallback(() => {
    if (!isOpenSticky) {
      sendAnalyticsEvent(VideoEvent.STICKY_PLAYER_OPEN, {
        label: StickyPlayerEventLabel.OPEN_SCROLL
      });
    }
    updateStickyContext({ openId: uniqueId });
  }, [isOpenSticky, sendAnalyticsEvent, updateStickyContext, uniqueId]);

  const handleStickyClose = useCallback(() => {
    updateStickyContext({ openId: null });
    sendAnalyticsEvent(VideoEvent.STICKY_PLAYER_CLOSE, {
      label: StickyPlayerEventLabel.SCROLL_CLOSE
    });
  }, [sendAnalyticsEvent, updateStickyContext]);

  useEffect(() => {
    if (shouldStickyVideoOpen(isInViewPort, playingId, uniqueId)) {
      handleStickyOpen();
    } else if (
      shouldStickyVideoClose(isInViewPort, playingId, openId, uniqueId)
    ) {
      handleStickyClose();
    }
  }, [
    isInViewPort,
    playingId,
    uniqueId,
    openId,
    handleStickyClose,
    handleStickyOpen
  ]);

  return {
    isStickyEnabled,
    isOpenSticky,
    playingId,
    openId,
    updateStickyContext,
    setOverrideStickyContext,
    setIsStickyEnabled,
    overrideStickyContext
  };
};
